import { Body, Controller, Get, Param, Patch, Post, Req, UseGuards } from '@nestjs/common';
import {
  ApiBearerAuth,
  ApiBody,
  ApiOperation,
  ApiParam,
  ApiResponse,
  ApiSecurity,
  ApiTags,
} from '@nestjs/swagger';
import {
  CheckoutSessionSummary,
  CompleteCheckoutSessionRequest,
  CreateCheckoutSessionRequest,
  UpdateCheckoutSessionRequest,
  OrderSummary,
} from '@cart/shared-types';
import { GuardedRequest, AuthGuard } from '../auth/auth.guard';
import { CheckoutService } from './checkout.service';

@ApiTags('Checkout')
@ApiBearerAuth()
@ApiSecurity('store-id')
@UseGuards(AuthGuard)
@Controller('checkout-sessions')
export class CheckoutController {
  constructor(private readonly checkoutService: CheckoutService) {}

  @Post()
  @ApiOperation({ summary: 'Create a checkout session from the current cart' })
  @ApiBody({
    schema: {
      type: 'object',
      properties: {
        email: { type: 'string', example: 'guest@example.com' },
        shippingRateId: { type: 'string' },
        discountCode: { type: 'string', example: 'SAVE10' },
        shippingAddress: {
          type: 'object',
          properties: {
            recipientName: { type: 'string', example: 'Jane Doe' },
            line1: { type: 'string', example: '123 Main St' },
            line2: { type: 'string' },
            city: { type: 'string', example: 'Austin' },
            state: { type: 'string', example: 'TX' },
            postalCode: { type: 'string', example: '78701' },
            country: { type: 'string', example: 'US' },
            phone: { type: 'string' },
          },
        },
      },
    },
  })
  @ApiResponse({ status: 201, description: 'Checkout session created with a unique token' })
  create(
    @Req() request: GuardedRequest,
    @Body() body: CreateCheckoutSessionRequest
  ): Promise<CheckoutSessionSummary> {
    return this.checkoutService.createSession(request.user.id, body);
  }

  @Get(':token')
  @ApiOperation({ summary: 'Get checkout session by token' })
  @ApiParam({ name: 'token', type: String, description: 'Session token from POST /checkout-sessions' })
  @ApiResponse({ status: 200, description: 'Returns checkout session with totals and line items' })
  @ApiResponse({ status: 404, description: 'Session not found or expired' })
  detail(@Param('token') token: string): Promise<CheckoutSessionSummary> {
    return this.checkoutService.findSession(token);
  }

  @Patch(':token')
  @ApiOperation({ summary: 'Update a checkout session (address, discount, shipping rate)' })
  @ApiParam({ name: 'token', type: String })
  @ApiResponse({ status: 200, description: 'Session updated with recalculated totals' })
  @ApiResponse({ status: 404, description: 'Session not found' })
  update(
    @Req() request: GuardedRequest,
    @Param('token') token: string,
    @Body() body: UpdateCheckoutSessionRequest
  ): Promise<CheckoutSessionSummary> {
    return this.checkoutService.updateSession(token, request.user.id, body);
  }

  @Post(':token/complete')
  @ApiOperation({ summary: 'Complete checkout — commits inventory, creates order and payment record' })
  @ApiParam({ name: 'token', type: String })
  @ApiBody({
    schema: {
      type: 'object',
      properties: {
        paymentProvider: { type: 'string', example: 'stripe' },
        paymentReference: { type: 'string', example: 'pi_xyz123' },
        paymentStatus: { type: 'string', enum: ['paid', 'pending'], example: 'paid' },
      },
    },
  })
  @ApiResponse({ status: 200, description: 'Order created — returns order summary' })
  @ApiResponse({ status: 400, description: 'Session already completed or expired' })
  complete(
    @Req() request: GuardedRequest,
    @Param('token') token: string,
    @Body() body: CompleteCheckoutSessionRequest
  ): Promise<OrderSummary> {
    return this.checkoutService.completeSession(token, request.user.id, body);
  }
}
